import moment from 'moment';
import ViewBase from '../../view-base.view';
import aiJpoModel from '../../../models/schedule/ai-jpo.model';
import languageModel from '../../../models/language.model';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature, Overlay } from 'ol';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import CircleStyle from 'ol/style/Circle';
import { Point, LineString } from 'ol/geom';
import Text from 'ol/style/Text';
import { fromLonLat } from 'ol/proj';
import { v4 as uuid } from 'uuid';
import 'CssFolder/schedule/jpo/ai-jpo.scss';

export default function MapTabView() {
    ViewBase.call(this);

    let map = null;
    let mapId = uuid();
    let filter = {};
    let selectedZone = '';
    let selectedGroup = '';
    let mapDate = '';
    let zones = [];
    let popOverlay = null;

    const ZONE_COLORS = [
        '#667eea', '#1D9E75', '#e74c3c', '#f39c12', '#9b59b6',
        '#3498db', '#e67e22', '#2ecc71', '#e91e63', '#00bcd4'
    ];

    let setDefaultDate = () => {
        mapDate = moment().format('YYYY-MM-DD');
    };

    let loadDropdowns = () => {
        aiJpoModel.getZonesDropdown({
            countryCode: filter.countryCode,
            bu: filter.bu
        })
            .then((result) => {
                zones = result || [];
            })
            .catch(() => { });
    };

    let loadGroups = () => {
        let req = { countryCode: filter.countryCode, bu: filter.bu };
        if (selectedZone) req.zoneId = selectedZone;
        aiJpoModel.getGroupsDropdown(req)
            .then((result) => {
                // Store in a local variable for the dropdown
                window['aiJpoMapGroups'] = result || [];
            })
            .catch(() => { });
    };

    let decodePolyline = (encoded) => {
        const pts = [];
        let i = 0, lat = 0, lng = 0;
        while (i < encoded.length) {
            let b, shift = 0, result = 0;
            do { b = encoded.charCodeAt(i++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
            lat += (result & 1) ? ~(result >> 1) : (result >> 1);
            shift = 0; result = 0;
            do { b = encoded.charCodeAt(i++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
            lng += (result & 1) ? ~(result >> 1) : (result >> 1);
            pts.push([lat / 1e5, lng / 1e5]);
        }
        return pts;
    };

    let initMap = () => {
        if (map) return;

        const popContainer = document.getElementById('map-popup-' + mapId);
        const popContent = document.getElementById('map-popup-content-' + mapId);
        const popCloser = document.getElementById('map-popup-closer-' + mapId);

        popOverlay = new Overlay({
            element: popContainer,
            autoPan: { animation: { duration: 250 } }
        });

        map = new Map({
            view: new View({
                zoom: 11,
                center: fromLonLat([100.5, 13.75])
            }),
            overlays: [popOverlay],
            layers: [
                new TileLayer({ source: new OSM({ attributions: '' }) })
            ],
            target: mapId
        });

        if (popCloser) {
            popCloser.onclick = () => {
                popOverlay.setPosition(undefined);
                popCloser.blur();
                return false;
            };
        }

        map.on('click', (e) => {
            const features = {};
            map.forEachFeatureAtPixel(e.pixel, (feature) => {
                if (feature.get('type') === 'shop' || feature.get('type') === 'start') {
                    features[feature.get('unique')] = feature;
                }
            });

            if (Object.keys(features).length === 0) {
                popOverlay.setPosition(undefined);
                return;
            }

            popContent.innerHTML = Object.keys(features).map((f) => {
                const data = features[f].get('data');
                return `<div><b>${data.storeCode || data.zoneName || ''}</b><br><small>${data.info || ''}</small></div>`;
            }).join(' ');

            popOverlay.setPosition(e.coordinate);
        });
    };

    let loadMap = () => {
        this.showLoading();

        let req = {
            countryCode: filter.countryCode,
            bu: filter.bu,
            date: mapDate
        };
        if (selectedZone) req.zoneId = selectedZone;
        if (selectedGroup) req.groupId = selectedGroup;

        aiJpoModel.getMapData(req)
            .then((data) => {
                this.hideLoading();

                if (!data.zones || !data.zones.length) {
                    document.getElementById(mapId).innerHTML =
                        `<div style="padding:80px;text-align:center;color:#999">
                            ${languageModel.getText('', 'No route data found.')}<br>
                            <small>${languageModel.getText('', 'Try a different date.')}</small>
                         </div>`;
                    if (map) { map.setTarget(undefined); map = null; }
                    return;
                }

                initMap();

                // Destroy old layers
                const layers = map.getLayers().getArray().slice(2);
                layers.forEach(l => map.removeLayer(l));

                const allPts = [];

                data.zones.forEach((zone, zi) => {
                    const color = ZONE_COLORS[zi % ZONE_COLORS.length];
                    const sLat = Number(zone.startLatitude), sLng = Number(zone.startLongitude);

                    // Starting point marker
                    if (sLat && sLng) {
                        const startCoord = fromLonLat([sLng, sLat]);
                        allPts.push(startCoord);
                        const startFeature = new Feature({
                            type: 'start',
                            geometry: new Point(startCoord),
                            unique: `start-${zi}`,
                            data: { zoneName: zone.zoneName, info: languageModel.getText('', 'Starting Point') }
                        });
                        const startSource = new VectorSource({ features: [startFeature] });
                        const startLayer = new VectorLayer({
                            source: startSource,
                            style: new Style({
                                image: new CircleStyle({
                                    radius: 12,
                                    fill: new Fill({ color: color }),
                                    stroke: new Stroke({ color: '#fff', width: 3 })
                                }),
                                text: new Text({
                                    text: String(zi + 1),
                                    font: 'bold 12px sans-serif',
                                    fill: new Fill({ color: '#fff' }),
                                    stroke: new Stroke({ color: '#000', width: 1 })
                                })
                            })
                        });
                        map.addLayer(startLayer);
                    }

                    (zone.groups || []).forEach((grp) => {
                        (grp.points || []).forEach((pt) => {
                            const lat = Number(pt.latitude), lng = Number(pt.longitude);
                            if (pt.type === 2 && lat && lng) {
                                const coord = fromLonLat([lng, lat]);
                                allPts.push(coord);
                                const shopFeature = new Feature({
                                    type: 'shop',
                                    geometry: new Point(coord),
                                    unique: `shop-${zi}-${pt.storeCode}`,
                                    data: {
                                        storeCode: pt.storeCode,
                                        info: `Grade: ${pt.grade || ''} | ${pt.startTime || ''} – ${pt.endTime || ''} | Dwell: ${pt.dwellTime || ''} min`
                                    }
                                });
                                const shopSource = new VectorSource({ features: [shopFeature] });
                                const shopLayer = new VectorLayer({
                                    source: shopSource,
                                    style: new Style({
                                        image: new CircleStyle({
                                            radius: 6,
                                            fill: new Fill({ color: color, opacity: 0.9 }),
                                            stroke: new Stroke({ color: '#fff', width: 2 })
                                        })
                                    })
                                });
                                map.addLayer(shopLayer);
                            }

                            // Route polyline
                            if (pt.prevGeometry && pt.prevGeometry.length > 0) {
                                try {
                                    const coords = decodePolyline(pt.prevGeometry);
                                    if (coords.length > 1) {
                                        const lineCoords = coords.map(c => fromLonLat([c[1], c[0]]));
                                        const routeFeature = new Feature({
                                            geometry: new LineString(lineCoords)
                                        });
                                        const routeSource = new VectorSource({ features: [routeFeature] });
                                        const routeLayer = new VectorLayer({
                                            source: routeSource,
                                            style: new Style({
                                                stroke: new Stroke({ color: color, width: 3.5, opacity: 0.75 })
                                            })
                                        });
                                        map.addLayer(routeLayer);
                                    }
                                } catch (_) { /* fallback: skip */ }
                            }
                        });
                    });
                });

                if (allPts.length > 1) {
                    const extent = olExtent(allPts);
                    map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 400 });
                }
            })
            .catch((error) => {
                this.handleError(error);
                this.hideLoading();
            });
    };

    let olExtent = (coords) => {
        const minLon = Math.min(...coords.map(c => c[0]));
        const maxLon = Math.max(...coords.map(c => c[0]));
        const minLat = Math.min(...coords.map(c => c[1]));
        const maxLat = Math.max(...coords.map(c => c[1]));
        return [minLon, minLat, maxLon, maxLat];
    };

    return {
        oninit: ({ attrs }) => {
            filter = attrs.filter;
            setDefaultDate();
            loadDropdowns();
        },
        oncreate: () => {
            // Map initializes on first loadMap call
        },
        onupdate: ({ attrs }) => {
            if (attrs.searchVersion !== filter._searchVersion) {
                filter = attrs.filter;
                filter._searchVersion = attrs.searchVersion;
                selectedZone = '';
                selectedGroup = '';
                loadDropdowns();
                loadMap();
            }
        },
        view: () => {
            const mapGroups = window['aiJpoMapGroups'] || [];

            return [
                // Filter bar
                m('.card.mb-3',
                    m('.card-body',
                        m('.row.g-4',
                            m('.col-sm-6.col-lg-3',
                                m('label.form-label', languageModel.getText('', 'Map Date')),
                                m('input.form-control.form-control-bg', {
                                    type: 'date',
                                    value: mapDate,
                                    onchange: (e) => { mapDate = e.target.value; }
                                })
                            ),
                            m('.col-sm-6.col-lg-3',
                                m('label.form-label', languageModel.getText('', 'Zone')),
                                m('select.form-control.form-control-bg', {
                                    value: selectedZone,
                                    onchange: (e) => {
                                        selectedZone = e.target.value;
                                        selectedGroup = '';
                                        loadGroups();
                                        loadMap();
                                    }
                                }, [
                                    m('option', { value: '' }, languageModel.getText('', 'ALL')),
                                    zones.map((z) => m('option', { value: z.id }, z.name || ''))
                                ])
                            ),
                            m('.col-sm-6.col-lg-3',
                                m('label.form-label', languageModel.getText('', 'Group')),
                                m('select.form-control.form-control-bg', {
                                    value: selectedGroup,
                                    onchange: (e) => { selectedGroup = e.target.value; loadMap(); }
                                }, [
                                    m('option', { value: '' }, languageModel.getText('', 'ALL')),
                                    mapGroups.map((g) => m('option', { value: g.id }, g.name || ''))
                                ])
                            ),
                            m('.col-sm-6.col-lg-3.d-flex.align-items-end.gap-2',
                                m('button.btn.btn-primary', { onclick: loadMap }, languageModel.getText('', 'Search'))
                            )
                        )
                    )
                ),

                // Map container
                m('.card',
                    m('.card-body.p-0',
                        m('.ai-jpo-map-container', { id: mapId }),
                        m('.ol-popup', { id: 'map-popup-' + mapId }, [
                            m('a.ol-popup-closer', { id: 'map-popup-closer-' + mapId }, '\u00d7'),
                            m('.ol-popup-content', { id: 'map-popup-content-' + mapId })
                        ])
                    )
                )
            ];
        }
    };
}
